import * as React from "react";
import { cn } from "@/lib/utils";

/* ─── GlassCard ─── */
export interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  intensity?: "light" | "medium" | "strong";
  hover?: boolean;
  glow?: boolean;
}

const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  ({ className, intensity = "medium", hover = true, glow = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        intensity === "light" && "glass-light",
        intensity === "medium" && "glass",
        intensity === "strong" && "glass-strong",
        hover && "glass-hover",
        glow && "glow-subtle",
        className
      )}
      {...props}
    />
  )
);
GlassCard.displayName = "GlassCard";

/* ─── GlassPanel ─── */
export interface GlassPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  atmosphere?: boolean;
}

const GlassPanel = React.forwardRef<HTMLDivElement, GlassPanelProps>(
  ({ className, atmosphere = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "glass p-6 md:p-8",
        atmosphere && "atmosphere-subtle",
        className
      )}
      {...props}
    />
  )
);
GlassPanel.displayName = "GlassPanel";

/* ─── GlassButton ─── */
export interface GlassButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost";
  size?: "default" | "lg" | "sm";
}

const GlassButton = React.forwardRef<HTMLButtonElement, GlassButtonProps>(
  ({ className, variant = "primary", size = "default", ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-2 font-sans transition-all duration-300 relative",
        variant === "primary" && "bg-primary text-background hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20 btn-glow",
        variant === "secondary" && "glass glass-hover text-foreground hover:border-primary/30",
        variant === "ghost" && "text-foreground/70 hover:text-foreground hover:bg-foreground/[0.05]",
        size === "sm" && "text-[10px] tracking-[0.2em] uppercase px-5 py-2.5",
        size === "default" && "text-[11px] tracking-[0.25em] uppercase px-7 py-3.5",
        size === "lg" && "text-xs tracking-[0.3em] uppercase px-10 py-4",
        className
      )}
      {...props}
    />
  )
);
GlassButton.displayName = "GlassButton";

/* ─── GlassFeatureTile ─── */
export interface GlassFeatureTileProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  title: string;
  description: string;
}

const GlassFeatureTile = React.forwardRef<HTMLDivElement, GlassFeatureTileProps>(
  ({ className, icon, title, description, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "group glass glass-hover p-8",
        "hover:border-primary/20",
        className
      )}
      {...props}
    >
      {icon && (
        <div className="w-12 h-12 flex items-center justify-center mb-5 glass-light rounded-xl group-hover:glow-subtle transition-all duration-300">
          {icon}
        </div>
      )}
      <h4 className="font-serif text-xl text-foreground mb-3 leading-snug">{title}</h4>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </div>
  )
);
GlassFeatureTile.displayName = "GlassFeatureTile";

/* ─── GlassNavigationBar ─── */
export interface GlassNavigationBarProps extends React.HTMLAttributes<HTMLDivElement> {
  compact?: boolean;
}

const GlassNavigationBar = React.forwardRef<HTMLDivElement, GlassNavigationBarProps>(
  ({ className, compact = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "glass-nav fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        compact ? "py-2" : "py-4",
        className
      )}
      {...props}
    />
  )
);
GlassNavigationBar.displayName = "GlassNavigationBar";

/* ─── GlassModal ─── */
export interface GlassModalProps extends React.HTMLAttributes<HTMLDivElement> {}

const GlassModal = React.forwardRef<HTMLDivElement, GlassModalProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "glass-strong p-6 md:p-8 animate-scale-in",
        className
      )}
      {...props}
    />
  )
);
GlassModal.displayName = "GlassModal";

export { GlassCard, GlassPanel, GlassButton, GlassFeatureTile, GlassNavigationBar, GlassModal };
